import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { adminOrders, settingsAPI } from '../../utils/api';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = ['pending','confirmed','processing','shipped','delivered','cancelled'];
const PAYMENT_OPTIONS = ['pending','paid','failed','refunded'];
const STATUS_COLORS = { pending:'#f6ad55',confirmed:'#68d391',processing:'#76e4f7',shipped:'#9f7aea',delivered:'#48bb78',cancelled:'#fc8181',paid:'#68d391',failed:'#fc8181',refunded:'#cbd5e0' };

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  // Default view hides orders where payment hasn't gone through (abandoned checkouts).
  // Toggle it off to see every row including pending/failed.
  const [showUnpaid, setShowUnpaid] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [siteSettings, setSiteSettings] = useState({});

  useEffect(() => {
    settingsAPI.getPublic()
      .then(rows => setSiteSettings(Object.fromEntries((rows || []).map(r => [r.setting_key, r.setting_value]))))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    adminOrders.getAll({ page, status, search, date_from: dateFrom, date_to: dateTo, limit: 15 })
      .then(d => { setOrders(d.orders); setTotal(d.total); setPages(d.pages); })
      .finally(() => setLoading(false));
  }, [page, status, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, newStatus, type = 'status') => {
    try {
      await adminOrders.updateStatus(id, type === 'status' ? { status: newStatus } : { payment_status: newStatus });
      toast.success('Order updated');
      load();
      if (selectedOrder?.id === id) setSelectedOrder(prev => ({ ...prev, [type]: newStatus }));
    } catch (err) { toast.error(err.message); }
  };

  const clearFilters = () => {
    setSearch(''); setStatus(''); setDateFrom(''); setDateTo(''); setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await adminOrders.exportAll({});
      const orderRows = (data.orders || []).map(o => ({
        'Order #': o.order_number,
        'Date': new Date(o.created_at).toLocaleString(),
        'Customer': o.shipping_full_name || 'Guest',
        'Phone': o.shipping_phone || '',
        'Email': o.guest_email || o.user_email || '',
        'Governorate': o.shipping_governorate || '',
        'Area': o.shipping_area || '',
        'Address': [o.shipping_address1, o.shipping_address2].filter(Boolean).join(', '),
        'Subtotal': parseFloat(o.subtotal || 0),
        'Discount': parseFloat(o.discount_amount || 0),
        'Shipping': parseFloat(o.shipping_cost || 0),
        'Total': parseFloat(o.total || 0),
        'Payment Method': o.payment_method || '',
        'Payment Status': o.payment_status,
        'Order Status': o.status,
        'Coupon': o.coupon_code || '',
      }));
      const itemRows = (data.items || []).map(i => {
        const o = (data.orders || []).find(x => x.id === i.order_id);
        return {
          'Order #': o?.order_number || i.order_id,
          'Product': i.product_name,
          'Variant': i.variant_name || '',
          'SKU': i.sku || '',
          'Qty': i.quantity,
          'Unit Price': parseFloat(i.unit_price),
          'Line Total': parseFloat(i.total_price),
        };
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(orderRows), 'Orders');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(itemRows), 'Items');
      const stamp = new Date().toISOString().slice(0,10);
      XLSX.writeFile(wb, `qaya-orders-${stamp}.xlsx`);
      toast.success(`Exported ${orderRows.length} orders`);
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const printInvoice = async (orderId) => {
    try {
      const order = await adminOrders.getDetail(orderId);
      const win = window.open('', '_blank', 'width=900,height=1100');
      if (!win) { toast.error('Allow pop-ups to print'); return; }
      win.document.write(buildInvoiceHTML(order, siteSettings));
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); }, 400);
    } catch (err) {
      toast.error(err.message || 'Could not load order');
    }
  };

  return (
    <div className="admin-orders">
      <div className="admin-page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>Orders <span className="page-count">({total})</span></h2>
        <button className="btn btn-primary" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting…' : '⬇ Export to Excel'}
        </button>
      </div>

      {/* Filters */}
      <div className="admin-filters">
        <input className="form-control" placeholder="Search order #, email, name..." value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ maxWidth: 260 }} />
        <select className="form-control" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ maxWidth: 150 }}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="filter-date">
          <span>From</span>
          <input type="date" className="form-control" value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
        </label>
        <label className="filter-date">
          <span>To</span>
          <input type="date" className="form-control" value={dateTo}
            onChange={e => { setDateTo(e.target.value); setPage(1); }} />
        </label>
        <label style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:'0.85rem', cursor:'pointer' }}>
          <input type="checkbox" checked={showUnpaid} onChange={e => setShowUnpaid(e.target.checked)} />
          Include unpaid
        </label>
        {(search || status || dateFrom || dateTo) && (
          <button className="action-btn" onClick={clearFilters}>Clear</button>
        )}
      </div>

      <div className="admin-card">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order #</th><th>Customer</th><th>Total</th>
                <th>Payment</th><th>Status</th><th>Date</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{margin:'0 auto'}} /></td></tr>
              ) : (() => {
                // Hide pending-payment orders (abandoned checkouts) unless "Include unpaid" is checked.
                // COD orders are also considered pending payment until marked paid — the admin can flip
                // them by editing the payment_status column or by ticking "Include unpaid".
                const visible = showUnpaid
                  ? orders
                  : orders.filter(o => o.payment_status === 'paid');
                if (visible.length === 0) {
                  return <tr><td colSpan="7" style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>
                    {orders.length > 0
                      ? `No paid orders (${orders.length} unpaid hidden — tick "Include unpaid" to see them)`
                      : 'No orders match these filters'}
                  </td></tr>;
                }
                return visible.map(o => (
                <tr key={o.id}>
                  <td><strong>{o.order_number}</strong></td>
                  <td>
                    <div>{o.shipping_full_name || 'Guest'}</div>
                    <small style={{color:'var(--text-muted)'}}>{o.guest_email || o.user_email}</small>
                  </td>
                  <td><strong>KD {parseFloat(o.total).toFixed(3)}</strong></td>
                  <td>
                    <span className="status-badge" style={{ background: STATUS_COLORS[o.payment_status]+'22', color: STATUS_COLORS[o.payment_status] }}>
                      {o.payment_status}
                    </span>
                  </td>
                  <td>
                    <select className="status-select" value={o.status}
                      onChange={e => updateStatus(o.id, e.target.value, 'status')}
                      style={{ borderColor: STATUS_COLORS[o.status] }}>
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
                    {new Date(o.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="action-btn" onClick={() => setSelectedOrder(o)}>View</button>
                      <button className="action-btn action-btn--icon" title="Print invoice" onClick={() => printInvoice(o.id)} aria-label="Print invoice">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 6 2 18 2 18 9"/>
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                          <rect x="6" y="14" width="12" height="8"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ));
              })()}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="admin-pagination">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span>Page {page} of {pages}</span>
            <button disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order {selectedOrder.order_number}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="action-btn" onClick={() => printInvoice(selectedOrder.id)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: 6 }}>
                    <polyline points="6 9 6 2 18 2 18 9"/>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                    <rect x="6" y="14" width="12" height="8"/>
                  </svg>
                  Print
                </button>
                <button onClick={() => setSelectedOrder(null)}>✕</button>
              </div>
            </div>
            <div className="modal-body">
              <div className="order-detail-grid">
                <div>
                  <h4>Shipping Address</h4>
                  <p>{selectedOrder.shipping_full_name}</p>
                  <p>{selectedOrder.shipping_phone}</p>
                  <p>{selectedOrder.shipping_address1}{selectedOrder.shipping_address2 ? `, ${selectedOrder.shipping_address2}` : ''}</p>
                  <p>{selectedOrder.shipping_area}, {selectedOrder.shipping_governorate}</p>
                </div>
                <div>
                  <h4>Payment</h4>
                  <p>Method: <strong>{selectedOrder.payment_method?.toUpperCase()}</strong></p>
                  <p>Status:
                    <select value={selectedOrder.payment_status}
                      onChange={e => updateStatus(selectedOrder.id, e.target.value, 'payment_status')}>
                      {PAYMENT_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </p>
                  {selectedOrder.sadad_transaction_id && <p>Sadad ID: {selectedOrder.sadad_transaction_id}</p>}
                </div>
              </div>
              <div className="order-amounts">
                <div><span>Subtotal</span><span>KD {parseFloat(selectedOrder.subtotal).toFixed(3)}</span></div>
                {parseFloat(selectedOrder.discount_amount) > 0 && (
                  <div><span>Discount ({selectedOrder.coupon_code})</span><span>-KD {parseFloat(selectedOrder.discount_amount).toFixed(3)}</span></div>
                )}
                <div><span>Shipping</span><span>KD {parseFloat(selectedOrder.shipping_cost).toFixed(3)}</span></div>
                <div className="total-row"><span>Total</span><span>KD {parseFloat(selectedOrder.total).toFixed(3)}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-page-header { margin-bottom: 20px; }
        .admin-page-header h2 { font-family: var(--font-serif); font-size: 1.5rem; }
        .page-count { font-size: 1rem; color: var(--text-muted); font-family: var(--font-sans); }
        .admin-filters { display: flex; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; align-items: flex-end; }
        .filter-date { display: flex; flex-direction: column; gap: 4px; font-size: 0.72rem; color: var(--text-muted); }
        .filter-date input { padding: 8px 10px; }
        .admin-card { background: white; border-radius: var(--radius-lg); padding: 20px; box-shadow: var(--shadow-sm); }
        .admin-table-wrap { overflow-x: auto; }
        .admin-table { width: 100%; border-collapse: collapse; }
        .admin-table th { text-align: left; padding: 10px 12px; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); border-bottom: 2px solid var(--border); white-space: nowrap; }
        .admin-table td { padding: 12px; font-size: 0.85rem; border-bottom: 1px solid #f5f5f5; vertical-align: middle; }
        .admin-table tr:hover td { background: #fafafa; }
        .status-badge { padding: 4px 10px; border-radius: 50px; font-size: 0.72rem; font-weight: 600; text-transform: capitalize; white-space: nowrap; }
        .status-select { border: 1px solid; border-radius: 6px; padding: 4px 8px; font-size: 0.8rem; font-family: var(--font-sans); background: white; cursor: pointer; text-transform: capitalize; }
        .action-btn { padding: 6px 14px; background: var(--cream); border-radius: 6px; font-size: 0.8rem; font-weight: 600; transition: background 0.2s; font-family: var(--font-sans); }
        .action-btn:hover { background: var(--gold); color: white; }
        .admin-pagination { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 20px; }
        .admin-pagination button { padding: 8px 16px; border-radius: 8px; background: var(--cream); font-size: 0.85rem; font-family: var(--font-sans); }
        .admin-pagination button:disabled { opacity: 0.4; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .modal { background: white; border-radius: var(--radius-lg); width: 100%; max-width: 540px; max-height: 90vh; overflow-y: auto; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid var(--border); }
        .modal-header h3 { font-family: var(--font-serif); }
        .modal-body { padding: 24px; }
        .order-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 20px; }
        .order-detail-grid h4 { font-weight: 700; margin-bottom: 8px; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); }
        .order-detail-grid p { font-size: 0.88rem; margin-bottom: 4px; }
        .order-amounts { border-top: 1px solid var(--border); padding-top: 16px; display: flex; flex-direction: column; gap: 8px; }
        .order-amounts div { display: flex; justify-content: space-between; font-size: 0.88rem; }
        .order-amounts .total-row { font-weight: 700; font-size: 1rem; border-top: 2px solid var(--border); padding-top: 10px; }
      `}</style>
    </div>
  );
};

const esc = (s) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

const buildInvoiceHTML = (order, settings) => {
  const items = order.items || [];
  const company = settings.company_name || settings.site_title || 'Qaya KW';
  const phone = settings.contact_phone || '';
  const email = settings.contact_email || '';
  const address = settings.contact_address || '';
  const fmt = (n) => `KD ${parseFloat(n || 0).toFixed(3)}`;
  const date = new Date(order.created_at).toLocaleString();
  const itemsHtml = items.map(i => `
    <tr>
      <td>${esc(i.product_name)}${i.variant_name ? `<br><small>${esc(i.variant_name)}</small>` : ''}</td>
      <td style="text-align:center">${i.quantity}</td>
      <td style="text-align:right">${fmt(i.unit_price)}</td>
      <td style="text-align:right">${fmt(i.total_price)}</td>
    </tr>
  `).join('');

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>Invoice ${esc(order.order_number)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #2c2416; margin: 40px; }
  .invoice { max-width: 720px; margin: 0 auto; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #d4943a; padding-bottom: 20px; margin-bottom: 30px; }
  .company { font-size: 1.4rem; font-weight: 700; color: #4a5e2a; }
  .company small { display: block; font-weight: 400; font-size: 0.85rem; color: #777; margin-top: 4px; }
  .meta { text-align: right; font-size: 0.9rem; }
  .meta h1 { font-size: 1.6rem; margin: 0 0 6px; color: #d4943a; }
  .meta p { margin: 2px 0; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
  .grid h4 { font-size: 0.78rem; text-transform: uppercase; letter-spacing: 1px; color: #888; margin: 0 0 8px; }
  .grid p { margin: 2px 0; font-size: 0.9rem; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  th { text-align: left; padding: 10px; background: #f7f3ec; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; color: #555; border-bottom: 2px solid #d4943a; }
  td { padding: 12px 10px; border-bottom: 1px solid #eee; font-size: 0.9rem; vertical-align: top; }
  td small { color: #888; }
  .totals { margin-left: auto; width: 280px; }
  .totals div { display: flex; justify-content: space-between; padding: 6px 0; font-size: 0.9rem; }
  .totals .total { font-weight: 700; font-size: 1.05rem; border-top: 2px solid #2c2416; padding-top: 10px; margin-top: 6px; }
  .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; color: #888; font-size: 0.8rem; }
  .badge { display: inline-block; padding: 3px 10px; border-radius: 12px; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; }
  .badge.paid { background: #d4f4e2; color: #276749; }
  .badge.unpaid { background: #fed7d7; color: #9b2c2c; }
  @media print { body { margin: 0; } .invoice { padding: 30px; } }
</style>
</head><body>
<div class="invoice">
  <div class="header">
    <div class="company">${esc(company)}<small>${esc(address)}<br>${esc(phone)} · ${esc(email)}</small></div>
    <div class="meta">
      <h1>INVOICE</h1>
      <p><strong>${esc(order.order_number)}</strong></p>
      <p>${esc(date)}</p>
      <p><span class="badge ${order.payment_status === 'paid' ? 'paid' : 'unpaid'}">${esc(order.payment_status)}</span></p>
    </div>
  </div>
  <div class="grid">
    <div>
      <h4>Bill To</h4>
      <p><strong>${esc(order.shipping_full_name)}</strong></p>
      <p>${esc(order.shipping_phone)}</p>
      <p>${esc(order.guest_email || order.user_email || '')}</p>
    </div>
    <div>
      <h4>Ship To</h4>
      <p>${esc([order.shipping_address1, order.shipping_address2].filter(Boolean).join(', '))}</p>
      <p>${esc(order.shipping_area)}, ${esc(order.shipping_governorate)}</p>
      <p>Payment: <strong>${esc((order.payment_method || '').toUpperCase())}</strong></p>
    </div>
  </div>
  <table>
    <thead><tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Unit</th><th style="text-align:right">Total</th></tr></thead>
    <tbody>${itemsHtml || '<tr><td colspan="4" style="text-align:center;color:#888">No items</td></tr>'}</tbody>
  </table>
  <div class="totals">
    <div><span>Subtotal</span><span>${fmt(order.subtotal)}</span></div>
    ${parseFloat(order.discount_amount) > 0 ? `<div><span>Discount${order.coupon_code ? ` (${esc(order.coupon_code)})` : ''}</span><span>-${fmt(order.discount_amount)}</span></div>` : ''}
    <div><span>Shipping</span><span>${fmt(order.shipping_cost)}</span></div>
    <div class="total"><span>Total</span><span>${fmt(order.total)}</span></div>
  </div>
  <div class="footer">Thank you for your order. ${esc(company)}</div>
</div>
</body></html>`;
};

export default AdminOrders;
